// Programmatic SEO landing pages. Each page is a real, working preset of the
// app (duration and/or sound) plus unique copy. Pages are pre-rendered to static
// HTML at build time (scripts/prerender.mjs) and the normal app loads on top.
//
// keyword/monthlySearches come from seo/keywords.csv (Google Ads Keyword Planner,
// US + UK + CA + AU, English) and are kept here for reference when expanding.

export type SoundMode = 'lofi' | 'focus' | 'forest' | 'white' | 'silent';

export interface LandingPage {
  path: string;
  keyword: string;
  monthlySearches: number;
  title: string; // <title>, keep under ~60 chars
  description: string; // meta description, ~150 chars
  h1: string;
  intro: string[];
  faqs: { q: string; a: string }[];
  preset: { duration?: number; musicMode?: SoundMode };
}

export const SITE_URL = 'https://kaizenflow.co';

const durationPage = (
  slug: string,
  minutes: number,
  label: string,
  keyword: string,
  monthlySearches: number,
  intro: string[],
  faqs: { q: string; a: string }[]
): LandingPage => ({
  path: `/timer/${slug}`,
  keyword,
  monthlySearches,
  title: `${label} Timer with Music | Kaizen Flow`,
  description: `A free ${label.toLowerCase()} timer with lofi music and a calm animated scene. Name one task, press enter, and focus until the timer ends. No sign-up.`,
  h1: `${label.toLowerCase()} timer`,
  intro,
  faqs,
  preset: { duration: minutes },
});

export const landingPages: LandingPage[] = [
  durationPage('10-minutes', 10, '10 Minute', '10 minute timer', 823000, [
    'Ten minutes is enough to clear one small thing you have been avoiding: an email reply, a tidy desk, the first paragraph of something bigger.',
    'Set the timer, write down the one thing, and press enter. Lofi music starts, the scene opens up, and the countdown sits quietly in the middle of the screen.',
  ], [
    { q: 'Can I add music to a 10 minute timer?', a: 'Yes. Lofi plays by default, and you can switch to focus music, nature sounds, white noise or silence before you start.' },
    { q: 'What happens when the 10 minutes are up?', a: 'A soft chime plays and you see a short summary of what you worked on, which you can save or share.' },
    { q: 'Is this good for a quick break instead?', a: 'It works for either. Leave the task blank, choose a nature sound, and use the ten minutes to step away.' },
  ]),
  durationPage('15-minutes', 15, '15 Minute', '15 minute timer', 823000, [
    'Fifteen minutes is the classic "just get started" block. It is short enough that starting feels easy and long enough to make real progress.',
    'Type what you will work on, press enter and the room fades in around you. The music keeps time with you; you only need to keep going until the clock runs out.',
  ], [
    { q: 'Does the 15 minute timer keep running in another tab?', a: 'Yes. The countdown shows in the browser tab title, so you can see the time left even when you switch away.' },
    { q: 'Can I pause the timer?', a: 'Press space to pause and again to resume. The music pauses with it.' },
    { q: 'Do I need an account?', a: 'No. It runs in your browser with no sign-up.' },
  ]),
  durationPage('20-minutes', 20, '20 Minute', '20 minute timer', 550000, [
    'Twenty minutes suits focused reading, a revision topic, or one chunk of a larger task. It feels like a manageable commitment, which makes it easier to begin.',
    'Name the task, press enter and let the scene take over. When the timer ends you get a small record of what you finished.',
  ], [
    { q: 'Is there a 20 minute timer with music?', a: 'Yes, this one. Lofi plays by default, or pick focus music, nature sounds, white noise or silence.' },
    { q: 'Can I change the length once I am here?', a: 'Use the − and + controls, or the up and down arrow keys, to change the time in 5 minute steps before you start.' },
    { q: 'Will it work on my phone?', a: 'Yes, it runs in mobile browsers as well as on desktop.' },
  ]),
  durationPage('25-minutes', 25, '25 Minute', '25 minute timer', 135000, [
    'Twenty-five minutes is the length of one pomodoro: a single focused block followed by a short break.',
    'Write the one thing you will move forward, press enter, and work until the timer ends. Then stand up, get some water and start the next one.',
  ], [
    { q: 'Why 25 minutes?', a: 'It is the standard block length in the Pomodoro Technique, long enough for real progress and short enough to stay sharp.' },
    { q: 'Does it include breaks?', a: 'The timer runs one focus block. When it ends, take a 5 minute break and start another session.' },
    { q: 'Can I listen to lofi while it runs?', a: 'Yes, lofi is the default sound. You can also choose focus music, nature, white noise or silence.' },
  ]),
  durationPage('30-minutes', 30, '30 Minute', '30 minute timer', 450000, [
    'Half an hour is a good default for focused work: enough time to get properly into a task without needing to plan a whole morning around it.',
    'Set your one task, press enter, and the ambient scene and music take care of the rest. The countdown stays in view without getting in the way.',
  ], [
    { q: 'Can I see the time left without looking at the page?', a: 'The remaining time shows in the browser tab title while the session runs.' },
    { q: 'Can I add more tasks during the 30 minutes?', a: 'Yes. Press A to add a task and T to tick off the current one.' },
    { q: 'Is it free?', a: 'Yes, and there is no sign-up.' },
  ]),
  durationPage('45-minutes', 45, '45 Minute', '45 minute timer', 90500, [
    'Forty-five minutes is a solid deep-work block, about the length of a class or a long revision session.',
    'Pick a sound that helps you settle, name the task and press enter. Controls fade away while you work and come back when you move the mouse.',
  ], [
    { q: 'What is the best music for a 45 minute focus session?', a: 'Many people prefer lofi or instrumental focus music because it has no lyrics. White noise or rain also work well if music distracts you.' },
    { q: 'Can I go fullscreen?', a: 'Press F for fullscreen and Esc to leave it.' },
    { q: 'What if I finish early?', a: 'Tick off your task with T and add the next one with A, or press Esc to end the session.' },
  ]),
  durationPage('1-hour', 60, '1 Hour', '1 hour timer', 201000, [
    'An hour is a serious block of focus. It is long enough to finish a draft, work through a problem set or make real headway on a project.',
    'Commit to one task, press enter and settle into the scene. Take a short break with space if you need it, and the music waits for you.',
  ], [
    { q: 'Is an hour too long to focus?', a: 'It depends on the task. If an hour feels like a stretch, try 45 minutes, or pause with space for a short break halfway through.' },
    { q: 'Does the music loop for the full hour?', a: 'Yes. The lofi stream and the longer tracks play for the whole session.' },
    { q: 'Do I need to keep the tab open?', a: 'Yes. The timer runs in your browser tab, so keep it open while you work.' },
  ]),
  durationPage('90-minutes', 90, '90 Minute', '90 minute timer', 14800, [
    'Ninety minutes is a long deep-work block, the kind of stretch for writing, coding or studying where getting into flow matters more than the clock.',
    'Choose a sound, name the single outcome you want, and press enter. The interface stays out of the way so it is just you, the scene and the work.',
  ], [
    { q: 'Should I take breaks in a 90 minute session?', a: 'You can pause any time with space. Many people work the full block and then take a longer break afterwards.' },
    { q: 'Which sound is best for long sessions?', a: 'White noise or nature sounds are easy to listen to for a long time; lofi works well if you like a steady beat.' },
    { q: 'Can I track several tasks?', a: 'Yes. Add tasks with A and tick them off with T as you go.' },
  ]),
  durationPage('2-hours', 120, '2 Hour', '2 hour timer', 60500, [
    'Two hours is a full work session, suited to exam revision, a big writing push or a long build.',
    'Set your main goal, press enter and let the calm scene carry you through. Pause with space whenever you need to stretch.',
  ], [
    { q: 'How do I stay focused for 2 hours?', a: 'Pick one clear outcome, remove distractions, and take short pauses when your attention dips. Steady background sound helps too.' },
    { q: 'Can I pause and come back?', a: 'Yes. Press space to pause; the timer and music wait until you resume.' },
    { q: 'Is there a longer option?', a: 'Two hours is the longest preset. For longer days, run several sessions back to back with breaks in between.' },
  ]),
  {
    path: '/pomodoro-timer',
    keyword: 'pomodoro timer',
    monthlySearches: 165000,
    title: 'Pomodoro Timer with Lofi Music | Kaizen Flow',
    description: 'A free 25 minute pomodoro timer with lofi music and a calm animated scene. Pick one task, focus for one pomodoro, then take a short break.',
    h1: 'Pomodoro timer',
    intro: [
      'The Pomodoro Technique breaks work into focused 25 minute blocks with short breaks in between. It makes starting easier and helps you notice how much you get done.',
      'This timer runs one pomodoro at a time. Name the task, press enter, work until the chime, then take five minutes away from the screen before the next one.',
    ],
    faqs: [
      { q: 'How does the Pomodoro Technique work?', a: 'Work on one task for 25 minutes, take a 5 minute break, and repeat. After four rounds, take a longer break of 15 to 30 minutes.' },
      { q: 'Does this timer handle the breaks?', a: 'It times the focus block. When it ends, take your break, then start the next pomodoro from the same page.' },
      { q: 'Can I use a different length?', a: 'Yes. Use the − and + controls or the arrow keys to change the time in 5 minute steps.' },
    ],
    preset: { duration: 25, musicMode: 'lofi' },
  },
  {
    path: '/study-timer',
    keyword: 'study timer',
    monthlySearches: 14800,
    title: 'Study Timer with Music | Kaizen Flow',
    description: 'A free study timer with lofi, focus music or rain sounds and a calm animated scene. Set one topic, press enter and study until the timer ends.',
    h1: 'Study timer',
    intro: [
      'Studying goes better in focused blocks with one clear topic at a time. This timer starts at 45 minutes, about the length of a lesson.',
      'Write the topic you are revising, choose the sound that helps you concentrate, and press enter. Your session ends with a small record of what you covered.',
    ],
    faqs: [
      { q: 'How long should a study session be?', a: 'Many students work well in 25 to 50 minute blocks with short breaks. Adjust the time with the − and + controls to suit you.' },
      { q: 'What should I listen to while studying?', a: 'Music without lyrics, like lofi or instrumental focus music, is a common choice. White noise and rain are good if you prefer no music.' },
      { q: 'Can I list more than one topic?', a: 'Yes. Add topics with A during the session and tick each off with T.' },
    ],
    preset: { duration: 45 },
  },
  {
    path: '/focus-timer',
    keyword: 'focus timer',
    monthlySearches: 6600,
    title: 'Focus Timer for Deep Work | Kaizen Flow',
    description: 'A minimal focus timer with ambient music and a calm animated scene. Commit to one task, press enter, and the interface disappears while you work.',
    h1: 'Focus timer',
    intro: [
      'Most timers sit in the corner while everything else competes for your attention. This one turns the whole screen into a quiet place to work.',
      'Commit to one task, press enter, and the controls fade away. Only the time and your task stay on screen, so it is easy to come back when your mind wanders.',
    ],
    faqs: [
      { q: 'What makes this different from a normal timer?', a: 'It is built around one task at a time, with an ambient scene and music, and the controls hide while you work.' },
      { q: 'What keyboard shortcuts are there?', a: 'Space pauses, T marks the task done, A adds a task, F toggles fullscreen and Esc ends the session.' },
      { q: 'How long is a focus session?', a: 'It starts at 30 minutes. Change it in 5 minute steps before you begin.' },
    ],
    preset: { duration: 30 },
  },
  {
    path: '/work-timer',
    keyword: 'work timer',
    monthlySearches: 5400,
    title: 'Work Timer with Background Music | Kaizen Flow',
    description: 'A free work timer with background music and a calm scene. Block out an hour, name the task and press enter. Runs in your browser with no sign-up.',
    h1: 'Work timer',
    intro: [
      'Blocking out time for one piece of work is one of the simplest ways to protect your day from meetings and messages.',
      'This timer starts at an hour. Name the task, press enter and work until it ends. The countdown shows in your browser tab so you can keep an eye on it from other windows.',
    ],
    faqs: [
      { q: 'Can I use it alongside other apps?', a: 'Yes. The remaining time shows in the tab title, and the music keeps playing while you work in other windows.' },
      { q: 'Can I turn the music off?', a: 'Choose silence before you start, or turn the volume down from the controls during the session.' },
      { q: 'Is my data stored anywhere?', a: 'There is no account. Your settings are kept in your browser.' },
    ],
    preset: { duration: 60 },
  },
  {
    path: '/study-with-me',
    keyword: 'study with me',
    monthlySearches: 8100,
    title: 'Study With Me: Lofi Timer Session | Kaizen Flow',
    description: 'A study with me session you control: a 50 minute timer, lofi music and an animated scene of someone working alongside you. Free, no sign-up.',
    h1: 'Study with me',
    intro: [
      'Study with me videos work because it is easier to focus when someone else is working too. This session gives you that feeling, with a timer you control.',
      'The scene shows someone at their desk working through the session with you. Name your task, press enter, and keep each other company until the timer ends.',
    ],
    faqs: [
      { q: 'How is this different from a study with me video?', a: 'You choose the length, the sound and your task, and you can pause whenever you need to.' },
      { q: 'How long is the session?', a: 'It starts at 50 minutes. Change it in 5 minute steps before you begin.' },
      { q: 'Does the scene change?', a: 'It follows the time of day: a daytime desk scene before 6pm and an evening one after.' },
    ],
    preset: { duration: 50, musicMode: 'lofi' },
  },
  {
    path: '/study-music',
    keyword: 'study music',
    monthlySearches: 40500,
    title: 'Lofi Study Music with a Focus Timer | Kaizen Flow',
    description: 'Lofi study music with a built-in focus timer and a calm animated scene. Set how long you will study, name the topic and press enter.',
    h1: 'Lofi study music',
    intro: [
      'Lofi is popular for studying because it is steady and has no lyrics, so it fills the silence without pulling your attention away.',
      'This page plays the 24/7 lofi hip hop radio with a timer built in. Set how long you will study, name the topic and press enter.',
    ],
    faqs: [
      { q: 'Is lofi good for studying?', a: 'Many people find it helps. It is calm and repetitive with no lyrics. If it distracts you, try white noise or rain instead.' },
      { q: 'Where does the music come from?', a: 'It streams from the lofi hip hop radio on YouTube, played inside the page.' },
      { q: 'Can I pick different music?', a: 'Yes. Switch between lofi, focus music, nature sounds, white noise and silence before you start.' },
    ],
    preset: { musicMode: 'lofi' },
  },
  {
    path: '/focus-music',
    keyword: 'focus music',
    monthlySearches: 27100,
    title: 'Focus Music with a Timer | Kaizen Flow',
    description: 'Instrumental focus music paired with a minimal timer and a calm animated scene. Choose your session length, name one task and press enter.',
    h1: 'Focus music',
    intro: [
      'Instrumental focus music gives your brain something steady to sit on while you work, without lyrics competing for attention.',
      'This page plays a long instrumental focus track with a built-in timer. Choose your session length, name one task and press enter.',
    ],
    faqs: [
      { q: 'What kind of music helps you focus?', a: 'Music without lyrics and with a steady tempo tends to work best. Try a few sounds to see which suits your work.' },
      { q: 'Can I control the volume?', a: 'Yes. Move the mouse during a session to show the controls, including a music volume slider.' },
      { q: 'Does the music stop when the timer ends?', a: 'The session ends with a chime and a summary; you can start another straight away.' },
    ],
    preset: { musicMode: 'focus' },
  },
  {
    path: '/white-noise-for-studying',
    keyword: 'white noise for studying',
    monthlySearches: 4400,
    title: 'White Noise for Studying, with a Timer | Kaizen Flow',
    description: 'Steady white noise for studying with a built-in timer and a calm scene. Mask background sound, set your session length and focus on one topic.',
    h1: 'White noise for studying',
    intro: [
      'White noise masks the background sounds that break concentration, like conversations, traffic and housemates, with one steady sound.',
      'Set how long you will study, name the topic and press enter. The white noise plays for the whole session and stops when you pause.',
    ],
    faqs: [
      { q: 'Does white noise help you study?', a: 'Many people find a constant sound makes sudden noises less distracting. It is worth trying if music pulls your attention.' },
      { q: 'How loud should white noise be?', a: 'Just loud enough to blur the background. Use the volume slider in the session controls.' },
      { q: 'Are there other sounds?', a: 'Yes. Switch to rain and nature, lofi, focus music or silence before starting.' },
    ],
    preset: { musicMode: 'white' },
  },
  {
    path: '/rain-sounds-for-studying',
    keyword: 'rain sounds for studying',
    monthlySearches: 1000,
    title: 'Rain Sounds for Studying, with a Timer | Kaizen Flow',
    description: 'Rain and thunder sounds for studying with a built-in focus timer and a calm animated scene. Free, in your browser, no sign-up.',
    h1: 'Rain sounds for studying',
    intro: [
      'Rain is one of the easiest sounds to work to: steady, natural and calming, without a beat to follow.',
      'This page plays rainforest rain and distant thunder with a timer built in. Set how long you will study, name the topic and press enter.',
    ],
    faqs: [
      { q: 'Why do rain sounds help with focus?', a: 'Rain is a steady, natural background sound that covers distractions and many people find it relaxing.' },
      { q: 'Is it real rain?', a: 'Yes, it is a long recording of rain and thunder in a rainforest.' },
      { q: 'Can I switch to music?', a: 'Yes. Change the sound to lofi, focus music, white noise or silence before you start.' },
    ],
    preset: { musicMode: 'forest' },
  },
];

const normalise = (p: string) => (p.length > 1 ? p.replace(/\/+$/, '') : p);

export const getLandingPage = (pathname: string): LandingPage | undefined =>
  landingPages.find((p) => p.path === normalise(pathname));
